import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Input, Textarea, Button } from '@components';
import { KnowledgeBase, KnowledgeBaseFormData } from '../types';

interface KnowledgeBaseFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: KnowledgeBaseFormData) => void;
  // Without a knowledge base the modal creates a new one
  knowledgeBase: KnowledgeBase | null;
}

export const KnowledgeBaseFormModal: React.FC<KnowledgeBaseFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  knowledgeBase,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    if (knowledgeBase && isOpen) {
      setFormData({
        name: knowledgeBase.name,
        description: knowledgeBase.description,
      });
    }
  }, [knowledgeBase, isOpen]);

  const isCreate = knowledgeBase === null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: formData.name.trim(),
      description: formData.description,
    });
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData({ name: '', description: '' });
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isCreate
              ? t('knowledgeBase.createNewKnowledgeBase')
              : t('knowledgeBase.editKnowledgeBase')}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="p-1 h-8 w-8">
            <span className="material-icons">close</span>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="edit-kb-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('knowledgeBase.knowledgeBaseNameRequired')}
            </label>
            <Input
              id="edit-kb-name"
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('knowledgeBase.enterKnowledgeBaseName')}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('knowledgeBase.descriptiveNameForKnowledgeBase')}
            </p>
          </div>

          <div>
            <label
              htmlFor="edit-kb-description"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('knowledgeBase.description')}
            </label>
            <Textarea
              id="edit-kb-description"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('knowledgeBase.enterKnowledgeBaseDescription')}
              rows={3}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('knowledgeBase.optionalDescriptionForKnowledgeBase')}
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('knowledgeBase.cancel')}
            </Button>
            <Button
              type="submit"
              variant="gradient-multi"
              className="flex items-center gap-2"
              disabled={!formData.name.trim()}
            >
              <span className="material-icons">{isCreate ? 'add' : 'save'}</span>
              {isCreate ? t('knowledgeBase.createKnowledgeBase') : t('knowledgeBase.saveChanges')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
